/**
 * apps/battle/client/src/main.tsx — entry point.
 *
 * Phase 4.2 M8 patch1 — Terminal is now the canonical student experience.
 * The legacy graph-mode student app is parked at /overview until we delete
 * it in a follow-up cleanup patch.
 *
 * Path dispatch (checked in order):
 *
 *   /admin[/*]      → AdminApp        (admin only — sme/student get a 403 screen)
 *   /review[/*]     → ReviewApp       (SME/admin only — students get a 403 screen with a "Play game" link)
 *   /reset[?...]    → ResetPasswordScreen   (M7 password-reset landing)
 *   /overview[/*]   → App             (legacy graph mode — parked here; will be removed)
 *   /terminal[/*]   → TerminalApp     (kept as an alias for / so old emails/bookmarks work)
 *   anything else   → TerminalApp     (default student landing; was App in M7)
 *
 * Session check happens here for /review and /admin only; App and TerminalApp
 * manage their own login flow. On unauth, we render <Login> and let the role
 * gate decide where to land on success.
 *
 * Note: SME/admin "Play game" buttons and the various wrong-role 403 links
 * navigate to '/' — that now lands on TerminalApp, which is the intended
 * student experience.
 */
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import { App } from './App.js';
import { Login } from './Login.js';
import { TerminalApp } from './terminal/TerminalApp.js';
import { ReviewApp } from './review/ReviewApp.js';
import { AdminApp } from './admin/AdminApp.js';
// Phase 4.2 M7 — password-reset landing page.
import { ResetPasswordScreen } from './terminal/screens/ResetPasswordScreen.js';
import './terminal/styles/terminal.css';
import { QueryClient, QueryClientProvider } from './review/query.js';
import { api } from './api.js';
import type { UserRole } from './api.js';

interface Session {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

const reviewQc = new QueryClient();

function ReviewRoot() {
  const [checking, setChecking] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    api
      .get<{ authenticated: boolean; student?: Session }>('/api/session')
      .then((resp) => {
        if (resp.authenticated && resp.student) setSession(resp.student);
      })
      .catch(() => { /* unauth */ })
      .finally(() => setChecking(false));
  }, []);

  async function handleLogout() {
    await api.post('/api/logout').catch(() => {});
    window.location.href = '/';
  }

  function handleLogin(s: Session) {
    // After successful login on /review:
    //  - SME/admin → stay on /review and mount the app
    //  - student   → bounce to / (terminal, the canonical student experience)
    if (s.role === 'sme' || s.role === 'admin') {
      setSession(s);
    } else {
      window.location.href = '/';
    }
  }

  if (checking) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', background: '#0f141c', color: '#97a3b6', fontFamily: 'system-ui, sans-serif' }}>
        Checking session…
      </div>
    );
  }

  if (!session) {
    return <Login onLogin={handleLogin} />;
  }

  // Authed but wrong role → 403-style screen with link back to game.
  if (session.role !== 'sme' && session.role !== 'admin') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', background: '#0f141c', color: '#e6ebf2', fontFamily: 'system-ui, sans-serif', textAlign: 'center', padding: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: 20 }}>Access restricted</h1>
          <p style={{ color: '#97a3b6', margin: '0 0 16px' }}>The review console is only available to SME and admin accounts.</p>
          <a href="/" style={{ color: '#3b82f6', textDecoration: 'none' }}>← Back to the game</a>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={reviewQc}>
      <ReviewApp
        reviewerName={session.name}
        onLogout={handleLogout}
        onExitToGame={() => { window.location.href = '/'; }}
      />
    </QueryClientProvider>
  );
}

// ---- Admin root (mirrors ReviewRoot; admin-only role gate) ---------------

function AdminRoot() {
  const [checking, setChecking] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    api
      .get<{ authenticated: boolean; student?: Session }>('/api/session')
      .then((resp) => {
        if (resp.authenticated && resp.student) setSession(resp.student);
      })
      .catch(() => { /* unauth */ })
      .finally(() => setChecking(false));
  }, []);

  async function handleLogout() {
    await api.post('/api/logout').catch(() => {});
    window.location.href = '/';
  }

  function handleLogin(s: Session) {
    // /admin is admin-only. Anyone else gets bounced.
    if (s.role === 'admin') {
      setSession(s);
    } else if (s.role === 'sme') {
      window.location.href = '/review';
    } else {
      window.location.href = '/';
    }
  }

  if (checking) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', background: '#0f141c', color: '#97a3b6', fontFamily: 'system-ui, sans-serif' }}>
        Checking session…
      </div>
    );
  }

  if (!session) {
    return <Login onLogin={handleLogin} />;
  }

  if (session.role !== 'admin') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', background: '#0f141c', color: '#e6ebf2', fontFamily: 'system-ui, sans-serif', textAlign: 'center', padding: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: 20 }}>Admin only</h1>
          <p style={{ color: '#97a3b6', margin: '0 0 16px' }}>The admin console is restricted to admin accounts.</p>
          <a href={session.role === 'sme' ? '/review' : '/'} style={{ color: '#3b82f6', textDecoration: 'none' }}>
            ← {session.role === 'sme' ? 'Back to review' : 'Back to the game'}
          </a>
        </div>
      </div>
    );
  }

  return (
    <AdminApp
      adminName={session.name}
      adminId={session.id}
      onLogout={handleLogout}
      onExitToGame={() => { window.location.href = '/'; }}
    />
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

const path = window.location.pathname;
const isAdmin = path.startsWith('/admin');
const isReview = path.startsWith('/review');
const isTerminal = path.startsWith('/terminal');
// Phase 4.2 M8 patch1 — legacy graph-mode student app parked at /overview.
const isOverview = path === '/overview' || path.startsWith('/overview/');
// Phase 4.2 M7 — /reset?token=... is the password-reset landing page.
const isReset = path === '/reset' || path.startsWith('/reset?') || path.startsWith('/reset/');

function ResetRoot() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';
  if (!token) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', background: '#0f141c', color: '#e6ebf2', fontFamily: 'system-ui, sans-serif', textAlign: 'center', padding: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: 20 }}>Invalid reset link</h1>
          <p style={{ color: '#97a3b6', margin: '0 0 16px' }}>This reset link is missing or malformed. Request a new one from the sign-in screen.</p>
          <a href="/terminal" style={{ color: '#3b82f6', textDecoration: 'none' }}>← Back to sign in</a>
        </div>
      </div>
    );
  }
  return <ResetPasswordScreen token={token} />;
}

// Phase 4.2 M8 patch1 — dispatcher order matters:
//   reset  > admin > review > overview > terminal > default(=TerminalApp)
// Default (no match) renders TerminalApp so that '/' lands on the terminal
// student experience. The legacy graph app is reachable only via /overview.
createRoot(rootEl).render(
  <React.StrictMode>
    {isReset
      ? <ResetRoot />
      : isAdmin
        ? <AdminRoot />
        : isReview
          ? <ReviewRoot />
          : isOverview
            ? <App />
            : isTerminal
              ? <TerminalApp />
              : <TerminalApp />}
  </React.StrictMode>,
);
